import React, { useEffect, useMemo } from 'react';
import {
  TableContainer,
  Table,
  TableHead,
  TableHeadCell,
  TableBody,
  TableRow,
  TableCell,
  Typography,
  Icon,
  Button,
  Paginator,
  Tooltip,
} from '@safedns/skydns-ui-react-lib';

import useSortable from '../../hooks/useSortable';
import usePagination from '../../hooks/usePagination';
import UsersListItem from './UsersListItem';
import FetchError from '../UI/fetch-error/FetchError';
import Loader from '../UI/loader/Loader';

import {
  useGetUserQuery,
  useGetCollectionsQuery,
  useGetUsersWithProfilesQuery,
} from '../../services/ADapi';

import { uid } from '../../uid';

import sprite from '../../icons/sprite.svg';

import { actionTableCellStyle } from '../../styles/tableStyles';

import styles from './Users.module.scss';

const tableHeadCellStyle = {
  textAlign: 'left',
  width: 120,
};

const filterUsers = (usersList, searchQuery) => {
  const lowerCaseSearchQuery = searchQuery.toLowerCase();

  return usersList.filter((user) => {
    return user.name.toLowerCase().includes(lowerCaseSearchQuery);
  });
};

const UsersList = ({ searchQuery, assignedFilter }) => {
  const {
    data: { user_id },
  } = useGetUserQuery();
  const { data: collections = [] } = useGetCollectionsQuery(user_id);
  const {
    data: allUsers = [],
    isLoading,
    isError,
  } = useGetUsersWithProfilesQuery(user_id);

  const usersAssignedToCollections = useMemo(() => {
    return collections.reduce((users, collection) => {
      users.push(...collection.ad_users);

      return users;
    }, []);
  }, [collections]);

  const filteredUsers = useMemo(() => {
    if (allUsers.length === 0) {
      return allUsers;
    }

    return allUsers.filter((user) => {
      if (assignedFilter === 'assigned') {
        return usersAssignedToCollections.includes(user.id);
      }

      if (assignedFilter === 'unassigned') {
        return !usersAssignedToCollections.includes(user.id);
      }

      if (!searchQuery) {
        return true;
      }

      const query = searchQuery.toLowerCase();

      return user.name.toLowerCase().includes(query);
    });
  }, [allUsers, assignedFilter, searchQuery, usersAssignedToCollections]);

  let { result, requestSort } = useSortable(filteredUsers);

  if (searchQuery.length > 0) {
    result = filterUsers(result, searchQuery);
  }

  const ITEMS_PER_PAGE = 10;
  const {
    firstContentIndex,
    lastContentIndex,
    totalPages,
    page,
    gaps,
    setPage,
    nextPage,
    prevPage,
  } = usePagination({
    contentPerPage: ITEMS_PER_PAGE,
    count: result.length,
  });

  useEffect(() => {
    if (searchQuery.length > 0) {
      setPage(0);
    }

    // TODO: figure out how to avoid disabling eslint
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [searchQuery]);

  const lastSeenHandler = () => {
    requestSort('last_seen');
  };

  if (isLoading) {
    return <Loader text="Please wait..." />;
  }

  if (isError) {
    return <FetchError />;
  }

  if (!result && !isLoading && !isError) {
    return (
      <p className={styles['users-list-empty']}>У вас еще нет AD агентов</p>
    );
  }

  return (
    <React.Fragment>
      <TableContainer>
        <Table sx={{ minWidth: 650 }} aria-label="users list">
          <TableHead>
            <TableRow>
              <TableHeadCell sx={tableHeadCellStyle}>
                Пользователи
              </TableHeadCell>
              <TableHeadCell sx={tableHeadCellStyle}>Коллекции</TableHeadCell>
              <TableHeadCell sx={tableHeadCellStyle}>
                <Typography
                  component="span"
                  sx={{
                    verticalAlign: 'middle',
                    color: 'inherit',
                    fontWeight: 500,
                  }}
                >
                  Профиль
                </Typography>
                <Tooltip
                  placement="right"
                  label="Активная политика указана из коллекции с наивысшим
                      приоритетом. Вы можете изменить приоритет коллекции на
                      вкладке Коллекции"
                  width="300px"
                  sx={{ textTransform: 'none' }}
                >
                  <Button variant="icon" sx={{ color: 'inherit' }}>
                    <Icon id="info" sprite={sprite} width={16} height={16} />
                  </Button>
                </Tooltip>
              </TableHeadCell>
              <TableHeadCell sx={{ textAlign: 'left', width: 80 }}>
                ПК
              </TableHeadCell>
              <TableHeadCell sx={{ textAlign: 'left', width: 80 }}>
                ОС
              </TableHeadCell>
              <TableHeadCell sx={{ textAlign: 'left', width: 180 }}>
                <span style={{ cursor: 'default' }} onClick={lastSeenHandler}>
                  <Button
                    onClick={lastSeenHandler}
                    variant="icon"
                    isIconButton
                    sx={{
                      marginLeft: 0,
                      padding: 0,
                      verticalAlign: 'middle',
                      color: 'inherit',
                    }}
                  >
                    <Icon
                      id="sort"
                      sprite={sprite}
                      width={18}
                      height={18}
                      viewBox="0 0 18 18"
                    />
                  </Button>
                  <Typography
                    component="span"
                    sx={{
                      verticalAlign: 'middle',
                      fontWeight: 'inherit',
                      color: 'inherit',
                    }}
                  >
                    Последний визит
                  </Typography>
                </span>
              </TableHeadCell>
              <TableHeadCell sx={actionTableCellStyle}></TableHeadCell>
            </TableRow>
          </TableHead>
          <TableBody>
            {result.length === 0 ? (
              <TableRow>
                <TableCell colSpan="6">
                  <Typography variant="subtitle2">
                    Нет пользователей, соответствующих заданному фильтру.
                  </Typography>
                </TableCell>
              </TableRow>
            ) : (
              result.slice(firstContentIndex, lastContentIndex).map((item) => (
                <UsersListItem
                  key={item.id}
                  id={item.id}
                  userName={item.name}
                  collections={collections.map((collection) => {
                    if (collection.ad_users.includes(item.id)) {
                      return (
                        <Typography key={uid()}>{collection.name}</Typography>
                      );
                    }

                    return null;
                  })}
                  policy={item.profile_id}
                  pc={item.hostname}
                  os={item.os_info}
                  last_seen={item.last_seen}
                />
              ))
            )}
          </TableBody>
        </Table>
      </TableContainer>
      {result.length > ITEMS_PER_PAGE && (
        <Paginator
          nextPage={nextPage}
          prevPage={prevPage}
          page={page}
          gaps={gaps}
          setPage={setPage}
          totalPages={totalPages}
        />
      )}
    </React.Fragment>
  );
};

export default UsersList;
